// SupportPage.tsx — /support, PUBLIC route (Growth slice, 1.21.60).
//
// TruXSocial has no ads and doesn't sell data — the only funding model is direct member
// support. This page is deliberately plain about the numbers rather than hyping a cause:
// what it costs, what it takes to cover that, and a single honest ask.

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Panel, Button, MetaRow, useToast } from '@/ui';
import { Icon } from '@/icons';
import { usePageTitle, useSession, useBilling } from '@/hooks';
import { startCheckout } from '@/features/premium/checkoutFlow';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 68ch;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  color: ${({ theme }) => theme.color.accentCyan};
  flex-shrink: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.display};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Intro = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const SectionTitle = styled.h2`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const SectionBody = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const CostFigure = styled.p`
  margin: 10px 0 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const ActionPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

const ComingSoonNote = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ManageLink = styled(Link)`
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.accentCyan};
`;

export function SupportPage() {
  usePageTitle('Support TruXSocial');
  const { isAuthenticated } = useSession();
  // Fix-round F4(c): the CTA is tier-aware — an existing Pro member must not
  // be offered "Become a supporter" again (checkout would mint a SECOND
  // subscription on the same account, billing them twice). `enabled` keeps
  // this public page from firing an authenticated request for a guest.
  const { billing, state: billingState, refresh } = useBilling({ enabled: isAuthenticated });
  const navigate = useNavigate();
  const toast = useToast();
  const [starting, setStarting] = useState(false);
  // Fix-round F1: set once Checkout answers "billing_not_configured". The
  // legacy $5.00 Payment Link is NOT a fallback — it charges a price we never
  // displayed and carries no client_reference_id, so the payment could never
  // be attributed to this account. Restores the pre-1.23.00 affordance:
  // disabled control + one plain visible "Supporter link coming soon." note.
  const [billingUnavailable, setBillingUnavailable] = useState(false);

  // 1.23.00: Premium IS supporting — one funnel. Signed in -> real Stripe
  // Checkout for Pro. Signed out -> signup first (an anonymous caller has no
  // account to attach a subscription to).
  async function handleBecomeSupporter() {
    if (!isAuthenticated) {
      navigate(`/signup?next=${encodeURIComponent('/support')}`);
      return;
    }
    setStarting(true);
    try {
      const outcome = await startCheckout();
      if (outcome.kind === 'unavailable') {
        setBillingUnavailable(true);
        toast("Checkout isn't set up yet. Try again soon.");
      } else if (outcome.kind === 'already_subscribed') {
        // 409: the server refused because this account is already Pro. Nothing
        // was charged, and no retry can succeed — so this must NOT reuse the
        // 'unavailable' affordance ("Supporter link coming soon"), which would
        // claim checkout is broken when it is working perfectly. The stale
        // client-side tier is the actual bug; refresh() re-reads /billing/me/
        // so the CTA flips to the already-supporting state on its own.
        toast("You're already supporting TruXSocial. Thank you.");
        refresh();
      } else if (outcome.kind === 'error') {
        toast('Something went wrong starting checkout. Please try again.');
      }
    } finally {
      setStarting(false);
    }
  }

  const alreadyPro = isAuthenticated && billing?.tier === 'pro';
  // Signed in but the tier is UNKNOWN (in flight or failed) — never render a
  // live checkout button on a guess.
  const tierUnknown = isAuthenticated && billingState !== 'ready';

  return (
    <Page>
      <HeaderRow>
        <IconBadge aria-hidden="true">
          <Icon name="support" size={22} />
        </IconBadge>
        <Title>Support TruXSocial</Title>
      </HeaderRow>

      <Intro>
        TruXSocial is independent: no ads, no data sales. The only thing that keeps it
        running is members who choose to pay for it directly.
      </Intro>

      <Panel>
        <SectionTitle>Why support</SectionTitle>
        <SectionBody>
          Free-speech platforms funded by ads or data brokers end up answering to
          advertisers, not members. TruXSocial doesn&apos;t run ads and doesn&apos;t sell
          your data. Supporter contributions are the entire revenue model, so the people
          paying for it are the people using it.
        </SectionBody>
      </Panel>

      <Panel>
        <SectionTitle>What it costs</SectionTitle>
        <SectionBody>
          Hosting costs about $16 a month to run. Four supporters at $5 a month cover
          that. That&apos;s the whole business model, laid out plainly rather than left
          vague.
        </SectionBody>
        <CostFigure>
          <MetaRow>
            <span>~$16/month hosting</span>
            <span>$5/month per supporter</span>
          </MetaRow>
        </CostFigure>
      </Panel>

      <ActionPanel>
        <SectionTitle>Become a supporter</SectionTitle>
        <SectionBody>
          Pro is $5/month, handled by Stripe. Cancel anytime.
        </SectionBody>
        {/* 1.23.00: Premium IS supporting — one funnel. Signed in triggers
            real Stripe Checkout; signed out goes to signup first. Real async
            action now (not a static href), so a plain Button is correct here
            — the old fixed <a href> only made sense back when the link was a
            known-in-advance static URL.

            Every "can't buy right now" branch below uses the SAME shape the
            pre-1.23.00 page used: an aria-disabled Button plus ONE plain,
            always-visible note. Deliberately NOT DisabledHint/aria-describedby
            — pairing that with an already-visible note made a screen reader
            announce the same sentence twice for one control. */}
        {alreadyPro ? (
          <>
            <Button
              variant="primary"
              aria-disabled="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              Become a supporter
            </Button>
            <ComingSoonNote>
              You&apos;re already supporting TruXSocial. Thank you.{' '}
              <ManageLink to="/premium">Manage your subscription</ManageLink>
            </ComingSoonNote>
          </>
        ) : billingUnavailable ? (
          <>
            <Button
              variant="primary"
              aria-disabled="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              Become a supporter
            </Button>
            <ComingSoonNote>Supporter link coming soon.</ComingSoonNote>
          </>
        ) : tierUnknown ? (
          <>
            <Button
              variant="primary"
              aria-disabled="true"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              Become a supporter
            </Button>
            {billingState === 'error' ? (
              <>
                <ComingSoonNote>We couldn&apos;t check your plan right now.</ComingSoonNote>
                <Button variant="secondary" onClick={refresh}>Try again</Button>
              </>
            ) : (
              <ComingSoonNote>Checking your plan…</ComingSoonNote>
            )}
          </>
        ) : (
          <>
            <Button
              variant="primary"
              disabled={starting}
              onClick={() => void handleBecomeSupporter()}
            >
              Become a supporter
            </Button>
            {!isAuthenticated && (
              <ComingSoonNote>Sign up first, then come back here to continue.</ComingSoonNote>
            )}
          </>
        )}
      </ActionPanel>
    </Page>
  );
}
